import useApiRequest from "@/lib/hooks/use-request";
import {
  SaleListPaginatedResponseDtoWithSelectableEntities,
  SaleListResponseDto,
  SaleSelectableEntitiesResponseDto,
} from "@/lib/interfaces/sales";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import SalesDataTable from "./sales-data-table";

export const SalesFetcher = ({
  searchQuery,
  setReload,
  reload,
  filters,
  setSelectableEntities,
}: {
  searchQuery: string;
  setReload: Dispatch<SetStateAction<boolean>>;
  reload: boolean;
  filters: string[];
  setSelectableEntities: Dispatch<SetStateAction<SaleSelectableEntitiesResponseDto | undefined>>;
}) => {
  const [items, setItems] = useState<SaleListResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const { apiRequest } = useApiRequest();
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: searchQuery.split("page=")[1] ? parseInt(searchQuery.split("page=")[1].split("&")[0]) - 1 : 0,
    pageSize: 10,
  });

  const [pageCount, setPageCount] = useState<number>(0);

  const getItemsInitial = useCallback(async () => {
    setInitLoading(true);
    const response = await apiRequest<SaleListPaginatedResponseDtoWithSelectableEntities>(`sales/initial`, "GET");
    setInitLoading(false);
    if (response.data) {
      setItems(response.data.sales.docs);
      setSelectableEntities(response.data.selectableEntities);
    }
  }, [apiRequest, setSelectableEntities]);

  const getItemsPaginated = useCallback(async () => {
    setIsLoading(true);
    let url = `sales?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
    if (filters.length > 0) {
      url += `&${filters.join("&")}`;
    }
    const response = await apiRequest<Paginated<SaleListResponseDto>>(url, "GET");
    if (response.data) {
      setItems(response.data.docs);
      setPageCount(response.data.pageCount);
    }
    setIsLoading(false);
  }, [apiRequest, filters, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    getItemsInitial();
  }, [getItemsInitial]);

  useEffect(() => {
    if (!initLoading) {
      getItemsPaginated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, getItemsPaginated, searchQuery, reload]);

  return (
    <DataFetchParent
      data={items}
      isLoading={initLoading || isLoading}
      renderElement={(response: SaleListResponseDto[]) => (
        <div className="w-full p-4">
          <SalesDataTable
            data={response}
            isLoading={initLoading || isLoading}
            pageCount={pageCount}
            pagination={pagination}
            setPagination={setPagination}
            setReload={setReload}
          />
        </div>
      )}
    />
  );
};

export default SalesFetcher;
